import * as React from 'react';
import axios from 'axios';
import { useHistory, useParams } from 'react-router-dom';
import { useAsyncFn, useSearchParam } from 'react-use';
import { EmptyState } from '@design-system/elements';
import { Button } from '@design-system/buttons';
import { isValidJwt, decodeJwt } from '@cincel/client';
import SignDocument from './SignDocument';

export default function GuardDocument() {
  const [state, renewAsyncLink] = useGuard();
  const [renovation, renewLink] = renewAsyncLink;
  const history = useHistory();

  if (state === 'bypass') return <SignDocument />;

  return (
    <div>
      {!renovation.value && (
        <EmptyState
          header="Tu invitación expiró"
          description="La invitación anterior ha caducado. Para recibir una nueva invitación, por favor haz clic en el botón de abajo"
          primaryAction={
            <Button
              isLoading={renovation.loading}
              color="#6344FF"
              className="text-white"
              variant="solid"
              onClick={() => {
                renewLink();
              }}>
              Enviar nueva invitación
            </Button>
          }></EmptyState>
      )}

      {renovation.value && (
        <EmptyState
          header="¡Tu invitación está lista!"
          description="Te hemos enviado una nueva a tu correo. Por favor, accede a través de ella."
          primaryAction={
            <Button
              color="#6344FF"
              className="text-white"
              variant="solid"
              onClick={() => {
                history.push('/');
              }}>
              Aceptar{' '}
            </Button>
          }></EmptyState>
      )}
    </div>
  );
}

const API_URL = process.env.REACT_APP_API_V3;

type LocationParams = Partial<{
  documentId: string;
  folderId: string;
  teamId: string;
}>;

async function renewInvitationLink(
  token: string | null,
  params: LocationParams & { invite_id: string },
) {
  return axios.get(
    API_URL +
    `/teams/${params.teamId}/folders/${params.folderId}/documents/${params.documentId}/invites/${params.invite_id}/notification`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
}

type State = 'bypass' | 'expired' | 'idle';

type JwtPayload = {
  sub: string;
  email: string;
  scope: string[];
  exp: number;
  iat: number;
};

function useGuard() {
  const token = useSearchParam('token');
  const params = useParams<LocationParams>();

  const renewAsyncLink = useAsyncFn(async () => {
    const { scope }: JwtPayload = decodeJwt<JwtPayload>(token);
    const invite_id = scope[0].split(':')[1];
    
    return renewInvitationLink(token, { ...params, invite_id });
  }, [token, params]);

  const state: State = React.useMemo(() => {
    // 1. If no token, we bypass (it might be a logged-in user or a public link)
    if (!token) return 'bypass';
    
    // 2. If token is present, we validate it locally
    if (isValidJwt(token)) return 'bypass';
    
    return 'expired';
  }, [token]);

  return [state, renewAsyncLink] as const;
}
